import { Injectable, OnDestroy } from '@angular/core';
import { ClusterPinModes, Folder, Layer, LayerTypes, MapWithState } from '../store/cop/models';
import { Document, WorkflowStep } from '../../core/store/models';
import { MapDrawingService } from './map-drawing.service';
import { LogLevel, Logger } from '../../core/services/logger/logger.model';
import { MapPositionService } from './map-position.service';
import { DatasetSubtype, DatasetType } from '../../core/store/models/dataset.model';
import { FolderUtils } from '../services/map/folder-utils';
import { MapDataWorkerService } from '../web-workers/client/map-data-worker.service';
import { LayerDataChangeType } from '../store/cop/models/layer-data-change-event.model';
import { map, takeUntil, tap } from 'rxjs/operators';
import { Feature } from './features/feature.model';
import { Subject } from 'rxjs';
import { MapLayerClusterService } from './cluster.service';
import { MapLabelService } from './label.service';
import { LayerMapFeatures, MapFeatures } from './models/layer-map-features.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { MAX_DOCS_TO_RENDER } from './layer-renderers/standard-layer.renderer';
import { MapPolygonVisibilityService } from './map-polygon-visibility.service';
import moment from 'moment';

export class MapLayerVisibilityService {
  layerVisibility: { [layerUId: string]: boolean } = {};
  workflowStatusVisibilitySettings: string = null;

  // track layer changes by type, section or layer
  workflowStatusVisibilitySettingsByLayer: {
    [layerUId: string]: {
      typeSettings: { [key: string]: string };
      sectionSettings: { [key: string]: string };
      layerSettings: { [key: string]: string };
    };
  } = {};
  // track document hidden visibility changes
  documentHiddenVisibilitySettings: { [layerUId: string]: string } = {};

  animatedWeatherLayers: { [layerUId: string]: boolean } = {};
  stepCache: { [stepUid: string]: string } = {};
  docVisibilityCache: { [key: string]: boolean } = {};
  layerVisibilitySettings: { [key: string]: LayerVisibilitySettings } = {};

  private mapState: MapWithState;
  private mapFeatures: MapFeatures;

  constructor(
    private mapDrawingService: MapDrawingService,
    private mapPositionService: MapPositionService,
    private clusterService: MapLayerClusterService,
    private labelService: MapLabelService,
    private workerService: MapDataWorkerService,
    private logger: Logger,
    private mapPolygonVisibilityService: MapPolygonVisibilityService
  ) {}

  public init(): void {
    this.layerVisibility = {};
    this.animatedWeatherLayers = {};
    this.stepCache = {};
    this.workflowStatusVisibilitySettings = null;
    this.documentHiddenVisibilitySettings = {};

    this.workflowStatusVisibilitySettingsByLayer = {};
  }

  public scanForVisibilityChanges(mapState: MapWithState, mapFeatures: MapFeatures): void {
    this.mapState = mapState;
    this.mapFeatures = mapFeatures;

    // need to reset these for every check
    this.docVisibilityCache = {};
    this.layerVisibilitySettings = {};

    const layerUIdsWithVisibilityChanges = this.scanMapStateForLayerVisibilityChanges();
    const layerUIdsWithAnimationChanges = this.scanMapStateForLayerAnimationChanges();
    const combinedChanges = layerUIdsWithVisibilityChanges
      .concat(layerUIdsWithAnimationChanges)
      .filter((item, pos, a) => a.indexOf(item) === pos);

    if (combinedChanges?.length > 0) {
      this.applyLayersVisibilityChanges(combinedChanges);
    }
  }

  private scanMapStateForLayerVisibilityChanges(): string[] {
    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'scanMapStateForLayerVisibilityChanges()');

    if (!!!this.mapState) {
      stopwatch.stop();
      return [];
    }

    const layersWithVisibilityChanges: string[] = [];

    const layers = this.mapState?.layers;
    if (!!!layers) {
      stopwatch.stop();
      return [];
    }

    for (let layer of layers.filter(l => l.isActive === true)) {
      const settings = this.getLayerVisibilitySettings(layer);
      let newVisibility = settings.isLayerVisible;

      // compare the current and new calculated visibility
      let currentVisibility = this.layerVisibility[layer.uId];

      if (
        (settings.canHideByWorkflow && this.isLayerWorkflowStatusVisibilityChanged(layer, settings)) ||
        (settings.canHideByDocument && this.isDocumentHiddenVisibilityChanged(layer, settings)) ||
        currentVisibility !== newVisibility
      ) {
        layersWithVisibilityChanges.push(layer.uId);
      }

      this.layerVisibility[layer.uId] = newVisibility;
    }

    stopwatch.stop();

    return layersWithVisibilityChanges;
  }

  private isLayerWorkflowStatusVisibilityChanged(layer: Layer, settings: LayerVisibilitySettings): boolean {
    let workflowStatusVisibilityChanged = false;

    if (!!!this.mapState?.workflowStatusVisibility) {
      return workflowStatusVisibilityChanged;
    }

    if (!!!this.workflowStatusVisibilitySettingsByLayer[layer.uId]) {
      this.workflowStatusVisibilitySettingsByLayer[layer.uId] = {
        typeSettings: {},
        sectionSettings: {},
        layerSettings: {}
      };
    }
    const workflowStatusVisibilitySettingsByLayer = this.workflowStatusVisibilitySettingsByLayer[layer.uId];

    const workflowStatusVisibilityByType = JSON.stringify(this.mapState?.workflowStatusVisibility[LayerTypes[layer.type]]);
    const workflowStatusVisibilityBySection = !!settings.section
      ? JSON.stringify(this.mapState?.workflowStatusVisibility[settings.section.uId])
      : null;
    const workflowStatusVisibilityByLayer = !!settings.section
      ? JSON.stringify(this.mapState?.workflowStatusVisibility[`${settings.section.uId}-${layer.uId}`])
      : null;

    if (
      workflowStatusVisibilitySettingsByLayer.typeSettings[LayerTypes[layer.type]] !== workflowStatusVisibilityByType ||
      (!!settings.section &&
        workflowStatusVisibilitySettingsByLayer?.sectionSettings[settings.section.uId] !== workflowStatusVisibilityBySection) ||
      (!!settings.section &&
        workflowStatusVisibilitySettingsByLayer?.layerSettings[`${settings.section.uId}-${layer.uId}`] !== workflowStatusVisibilityByLayer)
    ) {
      workflowStatusVisibilityChanged = true;
      workflowStatusVisibilitySettingsByLayer.typeSettings[LayerTypes[layer.type]] = workflowStatusVisibilityByType;
      if (!!settings.section) {
        workflowStatusVisibilitySettingsByLayer.sectionSettings[settings.section.uId] = workflowStatusVisibilityBySection;
        workflowStatusVisibilitySettingsByLayer.layerSettings[`${settings.section.uId}-${layer.uId}`] = workflowStatusVisibilityByLayer;
      }
    }

    return workflowStatusVisibilityChanged;
  }

  private isDocumentHiddenVisibilityChanged(layer: Layer, settings: LayerVisibilitySettings): boolean {
    let documentsByLayerChanged = false;
    if (!!this.mapState?.hiddenDocumentsByLayer) {
      const documentHiddenVisibilitySettings = JSON.stringify(this.mapState?.hiddenDocumentsByLayer[layer.uId]);
      if (documentHiddenVisibilitySettings !== this.documentHiddenVisibilitySettings[layer.uId]) {
        documentsByLayerChanged = true;
        this.documentHiddenVisibilitySettings[layer.uId] = documentHiddenVisibilitySettings;
      }
    }

    return documentsByLayerChanged;
  }

  public getLayerVisibilitySettings(layer: Layer): LayerVisibilitySettings {
    if (!!this.layerVisibilitySettings[layer.uId]) {
      return this.layerVisibilitySettings[layer.uId];
    }

    const settings: LayerVisibilitySettings = {
      canHideByWorkflow: false,
      canHideByDocument: false,
      isLayerVisible: true,
      section: null,
      dataset: null
    };
    settings.section = FolderUtils.getLayerSection(layer, this.mapState?.foldersSet);
    settings.dataset = !!this.mapState?.datasetsSet ? this.mapState?.datasetsSet[layer.datasetUId] : null;

    const layerState = (this.mapState?.layerStates ?? {})[layer.uId];

    settings.isLayerVisible = layerState?.isVisible;

    if (settings.isLayerVisible === true) {
      // the layer is visible in the LSD, lets check to
      // see if the items should be hidden based on zoom level
      const minDrawZoom =
        !!layer.displayOptions?.minDrawZoom && layer.displayOptions?.minDrawZoom > 0 ? layer.displayOptions?.minDrawZoom : 0;
      const maxDrawZoom =
        !!layer.displayOptions?.maxDrawZoom && layer.displayOptions?.maxDrawZoom > 0 ? layer.displayOptions?.maxDrawZoom : 25;

      settings.isLayerVisible =
        this.mapPositionService.position.zoomLevel >= minDrawZoom && this.mapPositionService.position.zoomLevel <= maxDrawZoom;

      if (settings.isLayerVisible == true) {
        if (layer.type === LayerTypes.Alert && !!this.mapState.workflowStatusVisibility) {
          if (
            !!this.mapState.workflowStatusVisibility[LayerTypes[layer.type]] ||
            !!(settings.section && this.mapState.workflowStatusVisibility[settings.section.uId]) ||
            !!(settings.section && this.mapState.workflowStatusVisibility[`${settings.section.uId}-${layer.uId}`])
          ) {
            settings.canHideByWorkflow = true;
          } else {
            if (!!this.mapState.hiddenDocumentsByLayer && !!this.mapState.hiddenDocumentsByLayer[layer.uId]) {
              settings.canHideByDocument = true;
            }
          }
        }
      }
    }

    this.layerVisibilitySettings[layer.uId] = settings;

    return settings;
  }

  public getVisibleDocuments(layer: Layer, documents: Document[], forceLayerIsVisible: boolean = null): Document[] {
    const settings = this.getLayerVisibilitySettings(layer);

    if (forceLayerIsVisible != null) {
      settings.isLayerVisible = forceLayerIsVisible;
    }

    return documents.filter(document => {
      return this.getDocumentVisibility(layer, document, settings);
    });
  }

  private scanMapStateForLayerAnimationChanges(): string[] {
    const layersWithAnimationChanges: string[] = [];

    if (!!!this.mapState) {
      return layersWithAnimationChanges;
    }
    const layers = this.mapState?.layers?.filter(l => l.type === LayerTypes.WeatherImagery && this.layerVisibility[l.uId]);
    if (!!!layers) {
      return layersWithAnimationChanges;
    }

    for (let layer of layers) {
      const currentIsAnimated = !!this.animatedWeatherLayers[layer.uId];
      const newIsAnimated = this.mapState.layerStates[layer.uId]?.isAnimated === true;
      if (currentIsAnimated !== newIsAnimated) {
        this.animatedWeatherLayers[layer.uId] = newIsAnimated;
        layersWithAnimationChanges.push(layer.uId);
      }
    }

    return layersWithAnimationChanges;
  }

  public isLayerVisible(layerUId: string): boolean {
    return this.layerVisibility && this.layerVisibility[layerUId];
  }

  public getVisibleLayerUIds(): string[] {
    return this.layerVisibility ? Object.keys(this.layerVisibility).filter(l => this.layerVisibility[l] === true) : [];
  }

  private applyLayersVisibilityChanges(layerUIds: string[], documentUIds?: string[]): void {
    const stopwatch = this.logger.startStopwatch(LogLevel.Info, 'setLayersVisibilityChanges()', { layerUIds });

    for (let layerUId of layerUIds) {
      const layerIsVisible = this.layerVisibility[layerUId] === true;
      const layer = this.mapState.layersSet[layerUId];

      if (layer.type === LayerTypes.WeatherImagery) {
        this.processWeatherTileVisibility(layer, layerIsVisible);
        continue;
      }

      if (!!layer.datasetUId) {
        const dataset = this.mapState?.datasetsSet[layer.datasetUId];

        if (dataset?.useTileServer === true) {
          this.processTopoTileVisibility(layer, layerIsVisible);
          continue;
        }

        // if this is a flight layer then turn on or off the change tracking
        if (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Flight) {
          if (layerIsVisible) {
            this.workerService.startTrackFlightLayerChanges({
              layer,
              dataset,
              layerQuickFilter: null
            });
          } else {
            this.workerService.stopTrackFlightLayerChanges({
              layer,
              dataset,
              layerQuickFilter: null
            });
          }
        }
      }
      this.applyDatasetLayerVisibilityChanges(layer, documentUIds);
    }

    stopwatch.stop();
  }

  public applyDatasetLayerVisibilityChanges(layer: Layer, documentUIds?: string[]): void {
    const layerState = this.mapState?.layerStates[layer.uId];
    if (!!!layerState) {
      return;
    }

    let documents = [];
    const allDocuments = Object.values(layerState.documents);
    if (!!documentUIds) {
      documents = allDocuments.filter(d => documentUIds.includes(d.uid));
    } else {
      documents = allDocuments;
    }

    for (let i = 0; i < documents.length; i += MAX_DOCS_TO_RENDER) {
      const documentsChunk = documents.slice(i, i + MAX_DOCS_TO_RENDER);

      setTimeout(() => {
        this.processDatasetLayerVisibilityChangesChunk(layer, documentsChunk);
      }, 0);
    }
  }

  private processDatasetLayerVisibilityChangesChunk(layer: Layer, documents: Document[]): void {
    const layerVisibility = this.isLayerVisible(layer.uId);

    this.clusterService.clearChanges(layer.uId);
    this.applyDocumentsVisibilityChanges(layer, documents);
    this.clusterService.applyChanges(layer.uId);

    if (layerVisibility === true && layer.displayOptions?.labelsVisible === true) {
      this.labelService.checkLayerLabelsDisplay(layer.uId);
    }

    if (layerVisibility) {
      this.mapPolygonVisibilityService.processMapMoveForPolygonVisibility([layer.uId]);
    }
  }

  private applyDocumentsVisibilityChanges(layer: Layer, documents: Document[], forceLayerIsVisible: boolean = null): void {
    const visibleDocuments = this.getVisibleDocuments(layer, documents, forceLayerIsVisible);
    const hiddenDocuments = documents.filter(d => !visibleDocuments.includes(d));

    if (this.mapDrawingService.renderOnlyVisible === true) {
      this.mapDrawingService.removeLayerDocuments(layer.uId, hiddenDocuments);
      this.mapDrawingService.addLayerDocuments(layer, visibleDocuments);
    } else {
      this.hideLayerDocuments(layer, hiddenDocuments);
      this.showLayerDocuments(layer, visibleDocuments);
    }
  }

  private showLayerDocuments(layer: Layer, documents: Document[]) {
    const layerFeatures = this.mapFeatures[layer.uId];
    if (!!!layerFeatures) {
      return;
    }

    for (let doc of documents) {
      const docFeatures = layerFeatures[doc.uid];
      if (!!!docFeatures) {
        continue;
      }

      let feature: Feature = null;
      if (docFeatures.features?.length > 0) {
        feature = docFeatures.features[0];
        feature.setVisible();
      } else {
        for (let circle of docFeatures.circles) {
          circle.setVisible(true);
        }

        for (let marker of docFeatures.markers) {
          marker.setVisible(true);
        }

        for (let polygon of docFeatures.polygons) {
          polygon.setVisible(true);
        }

        for (let polyline of docFeatures.polylines) {
          polyline.setVisible(true);
        }

        for (let rectangle of docFeatures.rectangles) {
          rectangle.setVisible(true);
        }
      }
    }
  }

  private hideLayerDocuments(layer: Layer, documents: Document[]) {
    const layerFeatures = this.mapFeatures[layer.uId];
    if (!!!layerFeatures) {
      return;
    }

    for (let doc of documents) {
      const docFeatures = layerFeatures[doc.uid];
      if (!!!docFeatures) {
        continue;
      }

      let feature: Feature = null;
      if (docFeatures.features?.length > 0) {
        feature = docFeatures.features[0];
        feature.setHidden();
      } else {
        for (let circle of docFeatures.circles) {
          circle.setVisible(false);
        }

        for (let marker of docFeatures.markers) {
          marker.setVisible(false);
        }

        for (let polygon of docFeatures.polygons) {
          polygon.setVisible(false);
        }

        for (let polyline of docFeatures.polylines) {
          polyline.setVisible(false);
        }

        for (let rectangle of docFeatures.rectangles) {
          rectangle.setVisible(false);
        }
      }
    }
  }

  private processWeatherTileVisibility(layer: Layer, isVisible: boolean): void {
    if (isVisible) {
      this.mapDrawingService.processAddEvent({
        type: LayerDataChangeType.Add,
        layerUId: layer.uId,
        changes: {},
        floor: null
      });
    } else {
      this.mapDrawingService.processRemoveEvent({
        type: LayerDataChangeType.Remove,
        layerUId: layer.uId,
        changes: {},
        floor: null
      });
    }
  }

  private processTopoTileVisibility(layer: Layer, isVisible: boolean): void {
    if (isVisible) {
      this.mapDrawingService.processAddEvent({
        type: LayerDataChangeType.Add,
        layerUId: layer.uId,
        changes: {},
        floor: this.mapDrawingService.floor
      });
    } else {
      this.mapDrawingService.processRemoveEvent({
        type: LayerDataChangeType.Remove,
        layerUId: layer.uId,
        changes: {},
        floor: this.mapDrawingService.floor
      });
    }
  }

  public isDocumentVisible(layer: Layer, document: Document): boolean {
    if (!!!layer || !!!document || !!!this.mapState?.foldersSet) {
      return false;
    }

    const documentVisibility = this.getDocumentVisibilityFromCache(layer.uId, document.uid);

    if (documentVisibility !== undefined) {
      return documentVisibility;
    }

    const settings = this.getLayerVisibilitySettings(layer);

    return this.getDocumentVisibility(layer, document, settings);
  }

  public processDocumentVisibilityChanges(document: Document, layer: Layer, mapState: MapWithState, forceLayerIsVisible: boolean = null) {
    this.clusterService.clearChanges(layer.uId);
    this.applyDocumentsVisibilityChanges(layer, [document], forceLayerIsVisible);
    this.clusterService.applyChanges(layer.uId);
  }

  private getDocumentVisibilityFromCache(layerUId: string, documentUId: string) {
    return this.docVisibilityCache[`${layerUId}-${documentUId}`];
  }

  private getDocumentVisibility(layer: Layer, document: Document, settings: LayerVisibilitySettings): boolean {
    if (!!!document) {
      return false;
    }
    let isVisible = true;

    if (settings.isLayerVisible === false) {
      isVisible = false;
    } else if (settings.canHideByWorkflow) {
      const stepName = !!this.stepCache[document.workflow_step_uid]
        ? this.stepCache[document.workflow_step_uid]
        : settings.dataset?.workflow?.workflowSteps?.find(ws => ws.uId === document.workflow_step_uid)?.statusName;
      this.stepCache[document.workflow_step_uid] = stepName;

      const isDocHiddenByWorkflow =
        stepName &&
        (!!this.mapState.workflowStatusVisibility[LayerTypes[layer.type]]?.includes(stepName) ||
          !!(settings.section && this.mapState.workflowStatusVisibility[settings.section.uId]?.includes(stepName)) ||
          !!(settings.section && this.mapState.workflowStatusVisibility[`${settings.section.uId}-${layer.uId}`]?.includes(stepName)));
      isVisible = !isDocHiddenByWorkflow;
    } else if (settings.canHideByDocument) {
      if (this.mapState.hiddenDocumentsByLayer[layer.uId].includes(document.uid)) {
        isVisible = false;
      }
    }

    if (isVisible && layer.displayOptions?.showInTimeline) {
      isVisible = this.isDocumentVisibleForTimelineControl(layer, document);
    }

    this.docVisibilityCache[`${layer.uId}-${document.uid}`] = isVisible;

    return isVisible;
  }

  private isDocumentVisibleForTimelineControl(layer: Layer, document: Document): boolean {
    const currentDate = this.mapDrawingService.currentTimelineDate;
    if (!currentDate) {
      return true;
    }

    const fromDateField = layer.displayOptions?.timelineFromField;
    const toDateField = layer.displayOptions?.timelineToField;
    if (!fromDateField && !toDateField) {
      return true;
    }

    const fromDateValue = document[fromDateField];
    const fromDate = !!fromDateValue ? moment(fromDateValue) : moment();

    const toDateValue = document[toDateField];
    const toDate = !!toDateValue ? moment(toDateValue) : moment();

    return currentDate.isBetween(fromDate, toDate);
  }

  public getVisibleDatasetWorkflowSteps(layer: Layer, settings: LayerVisibilitySettings) {
    let workflowsVisible = settings.dataset?.workflow?.workflowSteps?.map(wfs => wfs.uId) || [];

    if (settings?.isLayerVisible === false) {
      return [];
    } else if (!!settings?.canHideByWorkflow && !!settings?.dataset) {
      const wfSteps = settings.dataset?.workflow?.workflowSteps || [];

      wfSteps?.forEach(step => {
        const stepName = !!this.stepCache[step.uId]
          ? this.stepCache[step.uId]
          : settings.dataset.workflow?.workflowSteps?.find(ws => ws.uId === step.uId)?.statusName;
        this.stepCache[step.uId] = stepName;

        const isDocHiddenByWorkflow =
          stepName &&
          (!!this.mapState.workflowStatusVisibility[LayerTypes[layer.type]]?.find(s => s === stepName) ||
            !!(settings.section && this.mapState.workflowStatusVisibility[settings.section.uId]?.find(sn => sn === stepName)) ||
            !!(
              settings.section &&
              this.mapState.workflowStatusVisibility[`${settings.section.uId}-${layer.uId}`]?.find(sn => sn === stepName)
            ));

        if (!!isDocHiddenByWorkflow) {
          workflowsVisible = workflowsVisible.filter(wf => wf !== step.uId);
        }
      });
    }

    return workflowsVisible;
  }
}

export interface LayerVisibilitySettings {
  section?: Folder;
  dataset?: Dataset;
  canHideByWorkflow: boolean;
  canHideByDocument: boolean;
  isLayerVisible: boolean;
}
